import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "../context/auth";

type NotificationPreferences = {
	marketing: boolean;
	productUpdates: boolean;
	securityAlerts: boolean;
};

export function NotificationPreferencesPage() {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const { hasPermission } = useAuth();

	const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState<string | null>(null);

	const canUpdate = hasPermission("data", "update");

	const fetchPreferences = useCallback(
		async (signal?: AbortSignal) => {
			try {
				setError(null);
				const res = await fetch(
					`/api/tenant/${workspaceSlug}/notification-preferences`,
					{ credentials: "include", signal },
				);
				if (!res.ok) {
					throw new Error(
						res.status === 401
							? "Session expired. Please sign in again."
							: `Failed to load preferences (${res.status})`,
					);
				}
				const data = (await res.json()) as NotificationPreferences;
				setPrefs(data);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") return;
				setError(
					err instanceof Error ? err.message : "Failed to load preferences",
				);
			} finally {
				setLoading(false);
			}
		},
		[workspaceSlug],
	);

	useEffect(() => {
		const controller = new AbortController();
		fetchPreferences(controller.signal);
		return () => controller.abort();
	}, [fetchPreferences]);

	async function handleToggle(field: "marketing" | "productUpdates") {
		if (!prefs) return;
		const newValue = !prefs[field];

		setPrefs({ ...prefs, [field]: newValue });
		setSaving(field);
		setError(null);

		try {
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/notification-preferences`,
				{
					method: "PATCH",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ [field]: newValue }),
				},
			);
			if (!res.ok) {
				setPrefs({ ...prefs, [field]: !newValue });
				throw new Error(
					res.status === 403
						? "You don't have permission to update preferences."
						: `Failed to save (${res.status})`,
				);
			}
			const data = (await res.json()) as NotificationPreferences;
			setPrefs(data);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to save");
		} finally {
			setSaving(null);
		}
	}

	if (!hasPermission("data", "read")) {
		return (
			<div className="max-w-3xl mx-auto mt-8">
				<p
					className="text-sm text-gray-600"
					data-testid="notifications-no-access"
				>
					You don't have access to this page.
				</p>
			</div>
		);
	}

	return (
		<div className="max-w-3xl mx-auto mt-8" data-testid="notifications-page">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">
				Notification Preferences
			</h1>

			{error && (
				<div
					className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 mb-4"
					data-testid="notifications-error"
				>
					{error}
				</div>
			)}

			{loading ? (
				<div
					className="text-sm text-gray-500"
					data-testid="notifications-loading"
				>
					Loading preferences...
				</div>
			) : (
				prefs && (
					<section className="bg-white border border-gray-200 rounded-lg p-6">
						<h2 className="text-lg font-semibold text-gray-900 mb-4">
							Email Notifications
						</h2>
						<div className="space-y-4">
							<label className="flex items-center justify-between">
								<div>
									<span className="text-sm font-medium text-gray-900">
										Marketing
									</span>
									<p className="text-xs text-gray-500">
										Product announcements, promotions, and newsletters
									</p>
								</div>
								<div className="flex items-center gap-2">
									{saving === "marketing" && (
										<span className="text-xs text-gray-400">Saving...</span>
									)}
									<input
										type="checkbox"
										checked={prefs.marketing}
										onChange={() => handleToggle("marketing")}
										disabled={!canUpdate || saving !== null}
										className="rounded border-gray-300"
										data-testid="checkbox-marketing"
									/>
								</div>
							</label>

							<label className="flex items-center justify-between">
								<div>
									<span className="text-sm font-medium text-gray-900">
										Product Updates
									</span>
									<p className="text-xs text-gray-500">
										Feature releases, changelog, and platform updates
									</p>
								</div>
								<div className="flex items-center gap-2">
									{saving === "productUpdates" && (
										<span className="text-xs text-gray-400">Saving...</span>
									)}
									<input
										type="checkbox"
										checked={prefs.productUpdates}
										onChange={() => handleToggle("productUpdates")}
										disabled={!canUpdate || saving !== null}
										className="rounded border-gray-300"
										data-testid="checkbox-product-updates"
									/>
								</div>
							</label>

							<div className="flex items-center justify-between">
								<div>
									<span className="text-sm font-medium text-gray-900">
										Security Alerts
									</span>
									<p className="text-xs text-gray-500">
										Login activity, password changes, and security notices
										(always on)
									</p>
								</div>
								<input
									type="checkbox"
									checked={true}
									disabled
									className="rounded border-gray-300"
									data-testid="checkbox-security-alerts"
								/>
							</div>
						</div>
					</section>
				)
			)}
		</div>
	);
}
